import '../../../styles/restScreens.css';
import React, { useRef, useState } from 'react';
import Button3D from '../../ui/Button3D';
import Modal3D from '../../ui/Modal3D';
import { formatPhone } from '../../../lib/phone';
import { rsvpLabel } from '../eventLabels';
import { DEFAULT_COUNTRY, IMPORT_MAX_ROWS } from './guestConstants';
import { importPayload, readGuestCsv, templateCsv } from './guestCsv';
import { DEFAULT_EVENT_SETTINGS } from '../eventSettings';
import ProgramGuestImport from './ProgramGuestImport';
import EscapeGuard from './EscapeGuard';

const FIELD_NAMES = { first_name: 'First name', last_name: 'Last name', email: 'Email', phone: 'Phone', rsvp_status: 'RSVP', plus_ones_allowed: 'Plus-ones allowed', group: 'Group', notes: 'Notes', needs_shuttle: 'Needs shuttle', arrival_info: 'Arrival', file: 'File', row: 'Row' };

function readFileText(file) {
    if (typeof file.text === 'function') return file.text();
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result || ''));
        reader.onerror = () => reject(reader.error || new Error('The file could not be read.'));
        reader.readAsText(file);
    });
}

function downloadTemplate(settings) {
    try {
        const blob = new Blob([templateCsv(settings)], { type: 'text/csv;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = 'guest-list-template.csv';
        document.body.appendChild(link);
        link.click();
        link.remove();
        setTimeout(() => URL.revokeObjectURL(url), 0);
    } catch {
        // A browser that cannot make a download simply does nothing; the columns are also listed on screen.
    }
}

// Upload a CSV, see exactly what would happen to every row, fix the file if needed, then import. The database does the import in one go:
// if anything is wrong, nothing is imported. Exact duplicates (same name and email or phone) are skipped, not errors; different names sharing a contact are imported.
export default function GuestImportPopup({ engagementId = null, existingGuests = [], settings = DEFAULT_EVENT_SETTINGS, onImport, onClose, onDone }) {
    const [programFile, setProgramFile] = useState(null);
    const [fileName, setFileName] = useState('');
    const [parsed, setParsed] = useState(null);       // { rows, problems, counts }
    const [lines, setLines] = useState([]);
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState(null);
    const [failure, setFailure] = useState('');
    const input = useRef(null);

    const choose = async (event) => {
        const file = event.target.files?.[0];
        setResult(null); setFailure('');
        if (!file) return;
        if (/\.xlsx$/i.test(file.name)) { setProgramFile(file); return; }
        setFileName(file.name);
        try {
            const contents = await readFileText(file);
            if (/flight[ _#-]*(?:number|date|#)|date[ _-]*arriv|arriving.*carrier/i.test(contents.split(/\r?\n/)[0])) { setProgramFile(file); return; }
            const next = readGuestCsv(contents, { existing: existingGuests, defaultCountry: DEFAULT_COUNTRY, settings });
            setParsed(next);
            setLines(importPayload(next.rows).lines);
        } catch (error) {
            setParsed(null);
            setFailure(error?.message || 'The file could not be read.');
        }
    };

    const counts = parsed?.counts;
    const canImport = Boolean(parsed && !parsed.problems.length && counts.invalid === 0 && counts.ready > 0 && !busy && !result);

    const run = async () => {
        setBusy(true); setFailure('');
        try {
            const { payload } = importPayload(parsed.rows);
            const response = await onImport(payload);
            if (response?.ok) setResult(response);
            else {
                const messages = (response?.errors || []).map((item) => {
                    const line = lines[(item.row || 0) - 1];
                    return `${line ? `Line ${line}: ` : ''}${FIELD_NAMES[item.field] ? `${FIELD_NAMES[item.field]}: ` : ''}${item.message}`;
                });
                setFailure(messages.length ? `Nothing was imported. ${messages.slice(0, 5).join(' ')}` : 'Nothing was imported.');
            }
        } catch (error) {
            setFailure(error?.message || 'Nothing was imported. Please try again.');
        }
        setBusy(false);
    };

    const finish = () => { if (result) onDone?.(result); onClose(); };

    if (programFile) return <ProgramGuestImport file={programFile} engagementId={engagementId} onClose={onClose} onDone={onDone} />;

    return (
        <EscapeGuard>
            <Modal3D
                title="Import guests"
                subtitle="Upload a CSV, check it, then import. If anything is wrong, nothing is imported."
                size="wide"
                testId="guest-import-popup"
                onClose={finish}
                data-m6-layout="modals"
                data-m6-component="EventGuestImportPopup"
                actions={result ? (
                    <Button3D variant="go" onClick={finish}>Done</Button3D>
                ) : (
                    <>
                        <Button3D onClick={onClose} disabled={busy}>Cancel</Button3D>
                        <Button3D variant="go" onClick={run} disabled={!canImport}>{busy ? 'Importing…' : counts?.ready ? `Import ${counts.ready.toLocaleString('en-US')} ${counts.ready === 1 ? 'guest' : 'guests'}` : 'Import'}</Button3D>
                    </>
                )}
            >
                {result ? (
                    <div role="status" className="space-y-1" data-testid="import-result">
                        <p className="text-lg font-bold">Imported {result.inserted.toLocaleString('en-US')} {result.inserted === 1 ? 'guest' : 'guests'}.</p>
                        {result.duplicates > 0 && <p>{result.duplicates.toLocaleString('en-US')} {result.duplicates === 1 ? 'duplicate was' : 'duplicates were'} skipped (same name and email or phone).</p>}
                        {result.groups_created > 0 && <p>{result.groups_created} new {result.groups_created === 1 ? 'group was' : 'groups were'} created.</p>}
                    </div>
                ) : (
                    <>
                        <div className="flex flex-wrap items-center gap-2">
                            <Button3D variant="pill" onClick={() => downloadTemplate(settings)}>Download template</Button3D>
                            <Button3D variant="pill" onClick={() => input.current?.click()}>{fileName ? 'Choose another file' : 'Choose a CSV file'}</Button3D>
                            <input ref={input} type="file" accept=".xlsx,.csv,text/csv" className="sr-only" aria-label="Guest CSV file" onChange={choose} />
                            {fileName && <span className="min-w-0 break-all text-sm" data-testid="import-file-name">{fileName}</span>}
                        </div>
                        <p className="mt-2 text-sm">Columns we read: First name, Last name, Email, Phone, Group, RSVP, Plus-ones allowed, Notes{settings.shuttle_enabled !== false ? ', Needs shuttle (Yes or No) and Arrival' : ''}. Only First name is required. RSVP is Yes, No, No reply or Maybe (we also read Accepted, Declined and Pending). Up to {IMPORT_MAX_ROWS.toLocaleString('en-US')} guests per file.</p>
                        {failure && <p className="ia-resv-error" role="alert">{failure}</p>}
                        {parsed?.problems.map((problem) => <p key={problem} className="ia-resv-error" role="alert">{problem}</p>)}
                        {parsed && !parsed.problems.length && (
                            <>
                                <p className="mt-3 font-semibold" data-testid="import-summary">
                                    {counts.total.toLocaleString('en-US')} {counts.total === 1 ? 'row' : 'rows'}: {counts.ready.toLocaleString('en-US')} ready, {counts.duplicates.toLocaleString('en-US')} already on the list or repeated (skipped), {counts.invalid.toLocaleString('en-US')} need fixing.
                                </p>
                                {counts.shared > 0 && <p className="text-sm" data-testid="import-shared-summary">{counts.shared.toLocaleString('en-US')} {counts.shared === 1 ? 'row shares' : 'rows share'} a phone or email with a different guest. That is fine (couples and families do), and they will be imported.</p>}
                                {counts.invalid > 0 && <p className="text-sm">Fix the highlighted rows in your file and choose it again. Nothing is imported until every row is right.</p>}
                                <div className="ia-d2-scroll mt-2 max-h-[40vh] rounded-lg border border-[#3B4D73]" data-testid="import-preview">
                                    <table className="w-full min-w-[640px] text-left text-sm">
                                        <thead className="sticky top-0 bg-[#111B30]">
                                            <tr>{['Line', 'What happens', 'First name', 'Last name', 'Email', 'Phone', 'Group', 'RSVP', ...(settings.shuttle_enabled !== false ? ['Shuttle'] : [])].map((heading) => <th key={heading} className="px-2 py-1.5 text-xs font-bold uppercase tracking-wide">{heading}</th>)}</tr>
                                        </thead>
                                        <tbody>
                                            {parsed.rows.map((row) => (
                                                <tr
                                                    key={row.line}
                                                    data-testid="import-row"
                                                    data-state={row.state}
                                                    className={`border-t border-[#26365A] align-top ${row.state === 'invalid' ? 'border-l-4 border-l-[#FB923C] bg-[rgb(251_146_60/14%)]' : ''}`}
                                                >
                                                    <td className="px-2 py-1.5">{row.line}</td>
                                                    <td className="min-w-[200px] px-2 py-1.5">
                                                        {row.state === 'ready' && <span className="font-semibold text-[#22C55E]">Ready</span>}
                                                        {row.state === 'ready' && row.shared.map((note) => <div key={note} className="text-xs" data-testid="import-shared-note">{note}</div>)}
                                                        {row.state === 'duplicate' && <span className="font-semibold text-[#5EEAD4]">Skipped: {row.duplicate}</span>}
                                                        {row.state === 'invalid' && (
                                                            <ul className="space-y-0.5">
                                                                {row.errors.map((item) => <li key={item.field} className="font-semibold">{FIELD_NAMES[item.field]}: {item.message}</li>)}
                                                            </ul>
                                                        )}
                                                    </td>
                                                    <td className="px-2 py-1.5">{row.values.first_name}</td>
                                                    <td className="px-2 py-1.5">{row.values.last_name}</td>
                                                    <td className="px-2 py-1.5 break-all">{row.values.email}</td>
                                                    <td className="px-2 py-1.5 whitespace-nowrap">{row.values.phone ? formatPhone(row.values.phone, DEFAULT_COUNTRY) : ''}</td>
                                                    <td className="px-2 py-1.5">{row.values.group}</td>
                                                    <td className="px-2 py-1.5">{row.state === 'invalid' ? '' : rsvpLabel(row.values.rsvp_status)}</td>
                                                    {settings.shuttle_enabled !== false && <td className="px-2 py-1.5" data-testid="import-shuttle">{row.values.needs_shuttle === 'yes' ? `Yes${row.values.arrival_info ? ` · ${row.values.arrival_info}` : ''}` : ''}</td>}
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </>
                        )}
                    </>
                )}
            </Modal3D>
        </EscapeGuard>
    );
}
